/**
 * API クライアント
 */
import axios, { AxiosError } from 'axios';
import type { Transcription, TranscriptionListResponse, TranscriptSegment, User, DownloadFormat } from '../types';
import { parseContentDispositionFilename } from './contentDisposition';

/**
 * VITE_API_URL を正規化する。
 *
 * Cloudflare Pages 等のビルド環境変数に誤って `http://` の API URL が設定されると、
 * HTTPS で配信されているページから HTTP の API を叩いてしまい、ブラウザに
 * Mixed Content としてブロックされる。env を直すのが本筋だが、設定ミスに対する
 * 恒久的なセーフティネットとして、ページ自身が HTTPS のときは API URL も
 * HTTPS へ格上げする（ローカル開発は HTTP ページなので影響を受けない）。
 */
function normalizeApiBaseUrl(raw: string | undefined): string {
  const url = raw || '/api';
  const pageIsHttps =
    typeof window !== 'undefined' && window.location.protocol === 'https:';
  if (pageIsHttps && url.startsWith('http://')) {
    return 'https://' + url.slice('http://'.length);
  }
  return url;
}

const API_BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_URL);

// Axios インスタンス
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// リクエストインターセプター（認証トークン付与）
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// レスポンスインターセプター（エラーハンドリング）
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // 認証エラー時はトークンをクリアするのみ。
      // ここで window.location.href によるフルリロードを行うと、Supabase セッションが
      // 残っている場合に再マウント → /auth/me 401 → リロード の無限ループになる。
      // 画面遷移は AuthContext とルートガード（React Router）に委ねる。
      localStorage.removeItem('access_token');
    }
    return Promise.reject(error);
  }
);

// 認証
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },

  signup: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/signup', { email, password });
    return response.data;
  },

  oauthLogin: async (provider: 'google' | 'twitter') => {
    const response = await apiClient.post('/auth/oauth', { provider });
    return response.data;
  },

  logout: async () => {
    await apiClient.post('/auth/logout');
    localStorage.removeItem('access_token');
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  deleteAccount: async (reason?: string, feedback?: string): Promise<void> => {
    await apiClient.delete('/auth/me', {
      data: { reason, feedback },
    });
    localStorage.removeItem('access_token');
  },
};

// お問い合わせ
export interface ContactForm {
  name: string;
  email: string;
  category: string;
  message: string;
}

export const contactApi = {
  submit: async (data: ContactForm): Promise<void> => {
    await apiClient.post('/contact/', data);
  },
};

// TRPGシステム型
export interface TRPGSystem {
  id: string;
  name: string;
  // 読みがな。プルダウンの絞り込みでかな入力から漢字表記のシステムを引くために使う
  reading?: string;
}

// 署名付きアップロードURL発行レスポンス（v3.2.0 Unit 006・Unit 002 の UploadUrlResponse に対応）
export interface UploadUrlResponse {
  storage_key: string;
  signed_url: string;
  token: string;
  upload_ticket: string;
  expires_in: number;
}

// ジョブ作成メタ（任意項目）。num_speakers は有効整数（1〜8）のときのみ送る
export interface UploadMeta {
  sessionLog?: string;
  trpgSystem?: string;
  terms?: string;
  numSpeakers?: number;
}

// orchestrateUpload の依存注入（テスト容易性のため関数を差し替え可能にする）
export interface UploadDeps {
  getUploadUrl: (filename: string) => Promise<UploadUrlResponse>;
  uploadToSignedUrl: (grant: UploadUrlResponse, file: File, onProgress: (percent: number) => void) => Promise<void>;
  create: (payload: TranscriptionCreatePayload) => Promise<Transcription>;
}

// ③ ジョブ作成リクエスト（Unit 002 TranscriptionCreateFromKeyRequest に対応）
export interface TranscriptionCreatePayload {
  storage_key: string;
  upload_ticket: string;
  session_log?: string;
  trpg_system?: string;
  terms?: string;
  num_speakers?: number;
}

// 拡張子→Content-Type（再生/デコード選択のヒント。サーバはマジック番号で最終検証する）
// 動画はブラウザ変換に失敗した場合のみ原本アップロードされる（サーバ ffmpeg が音声抽出）。
const AUDIO_MIME_BY_EXT: Record<string, string> = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  flac: 'audio/flac',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  m4v: 'video/x-m4v',
  webm: 'video/webm',
};

function mimeForFilename(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return AUDIO_MIME_BY_EXT[ext] ?? 'application/octet-stream';
}

/**
 * 署名アップロードURLを絶対URLに正規化する。
 *
 * storage3 は token をクエリに内包した絶対URL（http(s)://...）を返すのが基本だが、
 * 実装差で相対パス（`/object/upload/sign/...`）が返り得る。相対時は
 * `{VITE_SUPABASE_URL}/storage/v1` を前置し、二重スラッシュを正規化する。
 * 最終的な実効性（CORS・ヘッダ）は Unit 007 の dev E2E で確認する。
 */
export function resolveSignedUploadUrl(signedUrl: string): string {
  if (/^https?:\/\//i.test(signedUrl)) {
    return signedUrl;
  }
  const base = (import.meta.env.VITE_SUPABASE_URL || '').replace(/\/+$/, '');
  const path = signedUrl.replace(/^\/+/, '');
  // 既に storage/v1 を含む相対パスならそのまま、含まなければ前置する
  const prefixed = path.startsWith('storage/v1/') ? path : `storage/v1/${path}`;
  return `${base}/${prefixed}`;
}

/**
 * Supabase Storage のエラー本文から利用者向け文言を導く（該当なしは null）。
 *
 * Supabase は HTTP status と本文の statusCode が食い違うことがある。サイズ超過は
 * 実測で **HTTP 400 + 本文 {"statusCode":"413","code":"EntityTooLarge"}** で返るため、
 * HTTP status だけを見ると原因不明の 400 になる。判定は本文の code を優先する。
 */
export function signedUploadErrorMessage(status: number, responseText: string): string | null {
  let code = '';
  try {
    code = (JSON.parse(responseText) as { code?: string }).code ?? '';
  } catch {
    // 本文が JSON でない場合（プロキシ由来の HTML など）は HTTP status のみで判断する
  }
  if (code === 'EntityTooLarge' || status === 413) {
    return 'ファイルサイズがサーバーの受け入れ上限を超えています。動画の場合は音声のみに変換してからアップロードしてください。';
  }
  return null;
}

/**
 * ② Supabase Storage へ署名URL経由で音声本体を直接 PUT する（進捗付き）。
 *
 * アプリの apiClient（axios）を使わず生 XHR で実装し、アプリの access_token を
 * Supabase ホストへ送出しない（署名URL/トークンが認可手段）。大容量を許容するため
 * timeout は 0（無効）。秘匿情報（URL/token）はエラー文言・ログに含めない。
 */
export function uploadToSignedUrl(
  grant: UploadUrlResponse,
  file: File,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = resolveSignedUploadUrl(grant.signed_url);
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.timeout = 0; // 大容量（最大2GB）で途中 abort しない
    xhr.setRequestHeader('Content-Type', mimeForFilename(file.name));
    xhr.setRequestHeader('x-upsert', 'true');

    let lastPercent = 0;
    xhr.upload.onprogress = (event: ProgressEvent) => {
      // lengthComputable=false / total<=0 のときは前回値を維持（NaN/Infinity を防ぐ）
      if (!event.lengthComputable || event.total <= 0) {
        return;
      }
      const raw = Math.round((event.loaded / event.total) * 100);
      const percent = Math.min(100, Math.max(0, raw));
      if (percent > lastPercent) {
        lastPercent = percent;
        onProgress(percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        // Supabase Storage のエラー本文（{statusCode, error, message}）は原因特定の唯一の手掛かり
        // （MIME 不許可・サイズ超過・署名不正はいずれも status だけでは判別できない）。
        // リクエスト側の署名URL/token は本文に含まれないため、コンソールへは出して良い。
        // 一方、利用者向け文言（reject する Error）には引き続き含めない。
        const body = xhr.responseText ?? '';
        console.error('Signed upload rejected:', xhr.status, mimeForFilename(file.name), body.slice(0, 500));
        reject(
          new Error(
            signedUploadErrorMessage(xhr.status, body) ??
              `アップロードに失敗しました（status: ${xhr.status}）`,
          ),
        );
      }
    };
    xhr.onerror = () => reject(new Error('アップロード中にネットワークエラーが発生しました'));
    xhr.onabort = () => reject(new Error('アップロードが中断されました'));
    xhr.ontimeout = () => reject(new Error('アップロードがタイムアウトしました'));

    xhr.send(file);
  });
}

/**
 * ①→②→③ を順に実行する純粋関数（UI 非依存・依存注入でテスト可能）。
 *
 * 各段の失敗時は後続を実行せず例外を送出する。副作用は onProgress 通知のみ。
 * 一覧キャッシュ無効化・遷移は呼び出し側（handleSubmit）の責務。
 */
export async function orchestrateUpload(
  deps: UploadDeps,
  file: File,
  meta: UploadMeta,
  onProgress: (percent: number) => void,
): Promise<Transcription> {
  // ① 署名URL・所有権チケット取得
  const grant = await deps.getUploadUrl(file.name);
  // ② Supabase へ直アップロード（進捗）
  await deps.uploadToSignedUrl(grant, file, onProgress);
  // ③ storage_key + upload_ticket でジョブ作成
  const payload: TranscriptionCreatePayload = {
    storage_key: grant.storage_key,
    upload_ticket: grant.upload_ticket,
  };
  if (meta.sessionLog) payload.session_log = meta.sessionLog;
  if (meta.trpgSystem) payload.trpg_system = meta.trpgSystem;
  if (meta.terms) payload.terms = meta.terms;
  // 話者数は 1〜8 の有効な整数のときのみ送る（範囲外/非整数/未指定はフィールドを送らず、サーバ側で自動推定）。
  // 多層防御として呼び出し側の検証に依存せず関数単体でも範囲を保証する（サーバは ge=1,le=8 で 422）。
  if (
    meta.numSpeakers !== undefined &&
    Number.isInteger(meta.numSpeakers) &&
    meta.numSpeakers >= 1 &&
    meta.numSpeakers <= 8
  ) {
    payload.num_speakers = meta.numSpeakers;
  }
  return deps.create(payload);
}

// 書き起こし
export const transcriptionApi = {
  // ① 署名付きアップロードURL・所有権チケット取得（v3.2.0 Unit 006）
  getUploadUrl: async (filename: string): Promise<UploadUrlResponse> => {
    const response = await apiClient.post('/transcriptions/upload-url', { filename });
    return response.data;
  },

  // ③ storage_key + upload_ticket でジョブ作成（v3.2.0 Unit 006・本体は送らない JSON）。
  // バックエンド実体は POST /transcriptions/（末尾スラッシュ）。厳密一致させ 307 リダイレクトを避ける。
  create: async (payload: TranscriptionCreatePayload): Promise<Transcription> => {
    const response = await apiClient.post('/transcriptions/', payload);
    return response.data;
  },

  getTRPGSystems: async (): Promise<TRPGSystem[]> => {
    const response = await apiClient.get('/transcriptions/trpg-systems');
    return response.data;
  },

  list: async (page = 1, pageSize = 10): Promise<TranscriptionListResponse> => {
    // バックエンド実体は GET /transcriptions/（末尾スラッシュ）。create と揃え 307 リダイレクトを避ける。
    const response = await apiClient.get('/transcriptions/', {
      params: { page, page_size: pageSize },
    });
    return response.data;
  },

  get: async (id: string): Promise<Transcription> => {
    const response = await apiClient.get(`/transcriptions/${id}`);
    return response.data;
  },

  // blob とあわせて、サーバが Content-Disposition に載せた元ファイル名を返す。
  // blob URL では元レスポンスのヘッダーがブラウザへ伝わらないため、保存名は呼び出し側で明示する必要がある。
  // ヘッダーを読めない場合（プロキシ除去・CORS 未公開など）は filename=null とし、呼び出し側でフォールバックする。
  download: async (
    id: string,
    format: DownloadFormat,
  ): Promise<{ blob: Blob; filename: string | null }> => {
    const response = await apiClient.get(`/transcriptions/${id}/download`, {
      params: { format },
      responseType: 'blob',
    });
    return {
      blob: response.data,
      filename: parseContentDispositionFilename(response.headers['content-disposition']),
    };
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/transcriptions/${id}`);
  },

  // 表示タイトルのリネーム。null / 空文字は「未設定へ戻す」＝表示はファイル名にフォールバック
  updateTitle: async (id: string, title: string | null): Promise<Transcription> => {
    const response = await apiClient.patch(`/transcriptions/${id}/title`, { title });
    return response.data;
  },

  // 話者名の一括リネーム（Unit 005）。生ラベル→表示名の最終状態を全置換で送る
  updateSpeakerNames: async (
    id: string,
    speakerNames: Record<string, string>,
  ): Promise<Transcription> => {
    const response = await apiClient.patch(`/transcriptions/${id}/speaker-names`, {
      speaker_names: speakerNames,
    });
    return response.data;
  },

  // セグメント単位の手動修正（Unit 007・unlimited のみ）。話者付け替え/テキストの最終状態を全置換で送る。
  // 送ったフィールドのみ更新（未指定は不変）。キーは配列index(文字列)。
  updateSegmentEdits: async (
    id: string,
    payload: {
      segment_speaker_overrides?: Record<string, string>;
      segment_text_overrides?: Record<string, string>;
      // v3.8.0 分割: 編集後の配列の最終状態。送るとサーバは override 2カラムを NULL にする
      edited_segments?: TranscriptSegment[];
    },
  ): Promise<Transcription> => {
    const response = await apiClient.patch(`/transcriptions/${id}/segments`, payload);
    return response.data;
  },
};

// ユーザー
export const userApi = {
  getProfile: async (): Promise<User> => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  updateProfile: async (displayName: string): Promise<User> => {
    const response = await apiClient.patch('/auth/me', {
      display_name: displayName,
    });
    return response.data;
  },

  getUsage: async () => {
    const response = await apiClient.get('/users/me/usage');
    return response.data;
  },

  updatePlan: async (planType: string, autoRenew = true): Promise<User> => {
    const response = await apiClient.post('/users/me/plan', {
      plan_type: planType,
      auto_renew: autoRenew,
    });
    return response.data;
  },
};

// お支払い履歴の1件（バックエンド PaymentHistoryItem に対応）
export interface PaymentHistoryItem {
  created_at: string;
  payment_type: 'subscription' | 'oneshot' | 'plan_change' | string;
  hours: number;
  /** ワンショットで購入した回数。古いバックエンドの応答には無いので 0 として扱う */
  sessions?: number;
  amount: number | null;
}

// ワンショット購入の内容。1回の購入は時間か回数のどちらか一方
export type OneshotOrder = { hours: number } | { sessions: number };

// confirm-session の応答（バックエンド plan_service の戻り値に対応）。
// ワンショットの granted / 付与量は「この決済で付与されたか・付与された量」を表し、
// Webhook が先に処理していても（status=already_processed）同じ値になる。
export type ConfirmSessionResult =
  | {
      payment_type: 'subscription';
      status: string;
      plan_type: string;
      message: string;
      hours_added: number;
    }
  | {
      payment_type?: undefined;
      status: 'success' | 'already_processed' | 'unlimited_plan';
      granted: boolean;
      hours_added: number;
      sessions_added: number;
      message: string;
    };

// アップグレードの事前試算（バックエンド PlanChangePreviewResponse に対応）。
// amount は今すぐ請求される日割り差額（円）。試算できなかった場合は null。
export interface PlanChangePreview {
  plan_type: string;
  amount: number | null;
}

// プラン変更の結果（バックエンド PlanChangeResponse に対応）。
// charged_amount は実際に請求された日割り差額（円）。取得できなかった場合は null。
export interface PlanChangeResult {
  message: string;
  plan_type: string;
  hours_limit: number;
  sessions_limit: number;
  prorated: boolean;
  charged_amount: number | null;
}

// 課金・決済
export const billingApi = {
  createCheckout: async (planType: string) => {
    const response = await apiClient.post('/billing/checkout', {
      plan_type: planType,
    });
    return response.data;
  },

  createOneshotPayment: async (order: OneshotOrder) => {
    const response = await apiClient.post('/billing/oneshot', null, {
      params: order,
    });
    return response.data;
  },

  confirmSession: async (sessionId: string): Promise<ConfirmSessionResult> => {
    const response = await apiClient.get('/billing/confirm-session', {
      params: { session_id: sessionId },
    });
    return response.data;
  },

  cancelSubscription: async () => {
    const response = await apiClient.post('/billing/cancel-subscription');
    return response.data;
  },

  // アップグレードで今すぐ請求される日割り差額の試算。状態は変わらない（読み取り専用）。
  // 受付条件は upgradeSubscription と同じで、下位・同一・解約予約中はサーバ側が拒否する。
  getUpgradePreview: async (planType: string): Promise<PlanChangePreview> => {
    const response = await apiClient.get('/billing/upgrade-preview', {
      params: { plan_type: planType },
    });
    return response.data;
  },

  // 上位プランへの即時変更（アップグレード）。上限だけが上がり、残り期間の差額は
  // 決済側が日割りで即時請求する。下位プラン・同一プランはサーバ側で拒否される。
  upgradeSubscription: async (planType: string): Promise<PlanChangeResult> => {
    const response = await apiClient.post('/billing/upgrade-subscription', {
      plan_type: planType,
    });
    return response.data;
  },

  // 下位プランへの変更予約（ダウングレード）。次回更新日から適用され、操作時点では
  // 枠・使用量・課金サイクルが変わらない。上位プラン・同一プランはサーバ側で拒否される。
  downgradeSubscription: async (planType: string) => {
    const response = await apiClient.post('/billing/downgrade-subscription', {
      plan_type: planType,
    });
    return response.data;
  },

  // 適用前のダウングレード予約の取り消し。契約価格が現在のプランへ戻る。
  cancelDowngrade: async () => {
    const response = await apiClient.post('/billing/cancel-downgrade');
    return response.data;
  },

  // 解約予約の取り消し（再開）。契約は満了日まで有効なため、満了前なら継続に戻せる。
  resumeSubscription: async () => {
    const response = await apiClient.post('/billing/resume-subscription');
    return response.data;
  },

  getConfig: async () => {
    const response = await apiClient.get('/billing/config');
    return response.data;
  },

  getHistory: async (): Promise<PaymentHistoryItem[]> => {
    const response = await apiClient.get('/billing/history');
    return response.data;
  },
};

// 管理者
export const adminApi = {
  getStats: async () => {
    const response = await apiClient.get('/admin/stats');
    return response.data;
  },

  getUsers: async (page = 1, pageSize = 50, search?: string, planType?: string) => {
    const response = await apiClient.get('/admin/users', {
      params: { page, page_size: pageSize, search, plan_type: planType },
    });
    return response.data;
  },

  getUser: async (userId: string) => {
    const response = await apiClient.get(`/admin/users/${userId}`);
    return response.data;
  },

  updateUserPlan: async (userId: string, planType: string) => {
    const response = await apiClient.patch(`/admin/users/${userId}/plan`, null, {
      params: { plan_type: planType },
    });
    return response.data;
  },

  toggleAdminStatus: async (userId: string, isAdmin: boolean) => {
    const response = await apiClient.patch(`/admin/users/${userId}/admin`, null, {
      params: { is_admin: isAdmin },
    });
    return response.data;
  },

  exportRevenue: async (year: number, month: number) => {
    const response = await apiClient.get('/admin/revenue/export', {
      params: { year, month },
      responseType: 'blob',
    });
    return response.data;
  },

  getTranscriptions: async (page = 1, pageSize = 50, status?: string, userId?: string) => {
    const response = await apiClient.get('/admin/transcriptions', {
      params: { page, page_size: pageSize, status, user_id: userId },
    });
    return response.data;
  },

  resetUserUsage: async (userId: string) => {
    const response = await apiClient.post(`/admin/reset-usage/${userId}`);
    return response.data;
  },

  resetAllExpiredUsage: async () => {
    const response = await apiClient.post('/admin/reset-all-usage');
    return response.data;
  },

  getUserPlans: async () => {
    const response = await apiClient.get('/admin/user-plans');
    return response.data;
  },

  getMaintenance: async (): Promise<MaintenanceState> => {
    const response = await apiClient.get('/admin/maintenance');
    return response.data;
  },

  updateMaintenance: async (payload: MaintenanceUpdate): Promise<MaintenanceState> => {
    const response = await apiClient.put('/admin/maintenance', payload);
    return response.data;
  },
};

export interface MaintenanceState {
  enabled: boolean;
  /** 終了予定時刻（ISO8601）。未設定なら null */
  ends_at: string | null;
  message: string | null;
}

export interface MaintenanceUpdate {
  enabled: boolean;
  ends_at?: string | null;
  message?: string | null;
}

export const maintenanceApi = {
  /**
   * メンテナンス状態の取得（認証不要）。
   *
   * 全ページの表示前に通るため、バックエンドが落ちている/応答しない場合に画面が
   * 固まらないよう短いタイムアウトを設ける。失敗時の扱いは呼び出し側（useMaintenance）が決める。
   */
  get: async (): Promise<MaintenanceState> => {
    const response = await apiClient.get('/maintenance', { timeout: 5000 });
    return response.data;
  },
};

// 特典コード（500人記念キャンペーン）
export interface RedemptionCode {
  /** 保存形（12桁・ハイフンなし）。表示時に formatRedemptionCode で整形する */
  code: string;
  grant_hours: number;
  grant_sessions: number;
  /** 引き換え日時（ISO8601）。未使用なら null（使用状態の唯一の判定軸） */
  redeemed_at: string | null;
}

export const redeemApi = {
  /** 自分に発行されたコード一覧。対象利用者を指定する手段は意図的に持たない */
  mine: async (): Promise<RedemptionCode[]> => {
    const response = await apiClient.get('/redeem/mine');
    return response.data;
  },

  /**
   * コードの引き換え。パスは末尾スラッシュなし（contact の '/contact/' とは異なる）。
   * 文言は付与量から画面側で組み立てる（API は文言を返さない）。
   */
  redeem: async (code: string): Promise<{ granted_hours: number; granted_sessions: number }> => {
    const response = await apiClient.post('/redeem', { code });
    return response.data;
  },
};

export default apiClient;
